import { Field, Formik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { Mutation } from 'react-apollo';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { UPSERT_DEPARTMENTAL_NUMBERS } from '../queries';
import { displayError, displaySuccess } from '../utilities';
import { BeatLoader } from 'react-spinners';

const styles = {
  formContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 3fr',
    gridRowGap: '0.3rem'
  },
  buttonRow: { gridColumn: '1 / span 2', textAlign: 'right' }
};

const validationSchema = Yup.object().shape({
  department: Yup.string()
    .required('Please enter valid department name')
    .matches(/^(?!\s)(.*\S)$/, 'Cannot start or end with whitespace')
    .min(3, 'Department name should be at least 3 characters long')
    .max(50, 'Department name must not exceed 50 characters'),
  level1Contact: Yup.string()
    .required('Please enter valid level 1 contact')
    .matches(/^(?!\s)(.*\S)$/, 'Cannot start or end with whitespace')
    .min(10, 'Level 1 contact name should be at least 10 characters long')
    .max(50, 'Level 1 contact name must not exceed 50 characters'),
  level2Contact: Yup.string()
    .optional()
    .matches(/^(?!\s)(.*\S)$/, 'Cannot start or end with whitespace')
    .min(10, 'Level 2 contact name should be at least 10 characters long')
    .max(50, 'Level 2 contact must not exceed 50 characters'),
  level3Contact: Yup.string()
    .optional()
    .matches(/^(?!\s)(.*\S)$/, 'Cannot start or end with whitespace')
    .min(10, 'Level 3 contact name should be at least three characters long')
    .max(50, 'Level 3 contact name must not exceed 50 characters')
});

const defaultInitValue = {
  department: '',
  level1Contact: '',
  level2Contact: '',
  level3Contact: ''
};

const DepartmentalNumbersEditModal = ({
  departmentalNumber,
  isOpen,
  hideModal,
  onSave
}) => {
  const departmentalNumberEdit = departmentalNumber || { ...defaultInitValue };
  const { id } = departmentalNumberEdit;

  return (
    <Mutation
      mutation={UPSERT_DEPARTMENTAL_NUMBERS}
      onCompleted={data => {
        onSave(data.upsertDepartmentalNumbers);
        displaySuccess(
          id ? 'Departmental numbers updated' : 'Departmental numbers created'
        );
      }}
      onError={e => {
        displayError(e.graphQLErrors[0].message);
      }}
      refetchQueries={() => ['GET_DEPARTMENTAL_NUMBERS']}
    >
      {(saveDepartmentalNumber, { loading }) => (
        <Modal isOpen={isOpen} toggle={hideModal}>
          <ModalHeader toggle={hideModal}>
            {id ? 'Edit Departmental Numbers' : 'Create Departmental Numbers'}
          </ModalHeader>
          <ModalBody>
            <Formik
              enableReinitialize
              initialValues={departmentalNumberEdit}
              validationSchema={validationSchema}
              onSubmit={values => {
                const cleanedValues = { ...values };
                delete cleanedValues.__typename;

                const input = id ? { id, ...cleanedValues } : cleanedValues;

                saveDepartmentalNumber({ variables: { input } });
              }}
            >
              {formik => (
                <form onSubmit={formik.handleSubmit}>
                  <div style={styles.formContainer}>
                    <div>
                      Department <span style={{ color: 'red' }}>*</span>
                    </div>
                    <div>
                      <Field name="department" style={{ width: '100%' }} />
                      {formik.touched.department &&
                        formik.errors.department && (
                          <div className="error">
                            {formik.errors.department}
                          </div>
                        )}
                    </div>

                    <div>
                      Level 1 Contact <span style={{ color: 'red' }}>*</span>
                    </div>
                    <div>
                      <Field name="level1Contact" style={{ width: '100%' }} />
                      {formik.touched.level1Contact &&
                        formik.errors.level1Contact && (
                          <div className="error">
                            {formik.errors.level1Contact}
                          </div>
                        )}
                    </div>

                    <div>Level 2 Contact</div>
                    <div>
                      <Field name="level2Contact" style={{ width: '100%' }} />
                      {formik.touched.level2Contact &&
                        formik.errors.level2Contact && (
                          <div className="error">
                            {formik.errors.level2Contact}
                          </div>
                        )}
                    </div>

                    <div>Level 3 Contact</div>
                    <div>
                      <Field name="level3Contact" style={{ width: '100%' }} />
                      {formik.touched.level3Contact &&
                        formik.errors.level3Contact && (
                          <div className="error">
                            {formik.errors.level3Contact}
                          </div>
                        )}
                    </div>

                    {formik.isValid &&
                    formik.values.department.trim().length > 0 &&
                    formik.values.level1Contact.trim().length > 0 ? (
                      <div style={styles.buttonRow}>
                        {loading ? (
                          <BeatLoader />
                        ) : (
                          <Button
                            type="submit"
                            color="primary"
                            disabled={loading || !formik.dirty}
                          >
                            {id ? 'Update' : 'Create'}
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </div>
                </form>
              )}
            </Formik>
          </ModalBody>
        </Modal>
      )}
    </Mutation>
  );
};

DepartmentalNumbersEditModal.propTypes = {
  departmentalNumber: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  hideModal: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default DepartmentalNumbersEditModal;
